import axios from '@/helpers/interceptors'
import type { AxiosResponse } from 'axios'
import type {
  CompanyListResponse,
  CompleteResetOtpRequest,
  CompleteResetOtpResponse,
  CompleteResetPasswordRequest,
  CompleteResetPasswordResponse,
  CreateEntityResponse,
  InitOtpRequest,
  InitOtpResponse,
  InitResetPasswordRequest,
  InitResetPasswordResponse,
  LoginRequest,
  LoginResponse,
  SelectCompanyRequest,
  SelectCompanyResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from './types'

/** Strip everything except digits — backend wants raw international digits. */
function digitsOnly(phone: string): string {
  return phone.replace(/[^\d]/g, '')
}

/**
 * HTTP surface for login + forgot-password flows.
 *
 * All methods return the full `AxiosResponse<T>` (not just `data`) so callers
 * can inspect `status` if they need to. Error handling stays the caller's
 * responsibility — the shared interceptor and `mapAuthError` cover that.
 */
export const authApi = {
  initOtp(req: InitOtpRequest): Promise<AxiosResponse<InitOtpResponse>> {
    return axios.post<InitOtpResponse>('/api/auth/init-otp', {
      ...req,
      phone: digitsOnly(req.phone),
    })
  },

  verifyOtp(req: VerifyOtpRequest): Promise<AxiosResponse<VerifyOtpResponse>> {
    return axios.post<VerifyOtpResponse>('/api/auth/verify-otp', req)
  },

  login(req: LoginRequest): Promise<AxiosResponse<LoginResponse>> {
    return axios.post<LoginResponse>('/api/auth/login', {
      ...req,
      phone: digitsOnly(req.phone),
    })
  },

  getCompanies(): Promise<AxiosResponse<CompanyListResponse>> {
    return axios.get<CompanyListResponse>('/api/companies/')
  },

  selectCompany(req: SelectCompanyRequest): Promise<AxiosResponse<SelectCompanyResponse>> {
    return axios.post<SelectCompanyResponse>('/api/auth/current-company', req)
  },

  createEntity(): Promise<AxiosResponse<CreateEntityResponse>> {
    return axios.post<CreateEntityResponse>('/api/registration/create-entity')
  },

  initOtpResetPassword(req: InitResetPasswordRequest): Promise<AxiosResponse<InitResetPasswordResponse>> {
    return axios.post<InitResetPasswordResponse>('/api/auth/step1/init-otp-reset-password', {
      ...req,
      phone: digitsOnly(req.phone),
    })
  },

  completeOtpResetPassword(req: CompleteResetOtpRequest): Promise<AxiosResponse<CompleteResetOtpResponse>> {
    return axios.post<CompleteResetOtpResponse>('/api/auth/step2/complete-otp-reset-password', req)
  },

  completeResetPassword(req: CompleteResetPasswordRequest): Promise<AxiosResponse<CompleteResetPasswordResponse>> {
    return axios.post<CompleteResetPasswordResponse>('/api/auth/step3/complete-reset-password', req)
  },
}
